import { useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { toast } from "sonner";
import { CheckCircle, XCircle } from "lucide-react";
import { API } from "../../apiConfig";


const WholesaleTab = ({ applications, token, onRefresh }) => {
  const headers = { Authorization: `Bearer ${token}` };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const approveWholesale = async (userId, discount = 0.20) => {
    try {
      await axios.put(`${API}/admin/wholesale/${userId}/approve?discount=${discount}`, {}, { headers });
      toast.success("Wholesale application approved");
      onRefresh();
    } catch (error) {
      toast.error("Failed to approve application");
    }
  };

  const rejectWholesale = async (userId) => {
    try {
      await axios.put(`${API}/admin/wholesale/${userId}/reject`, {}, { headers });
      toast.success("Wholesale application rejected");
      onRefresh();
    } catch (error) {
      toast.error("Failed to reject application");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-emerald-500">Approved</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500">Pending</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500">Rejected</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <Card className="rounded-2xl" data-testid="wholesale-tab">
      <CardHeader>
        <CardTitle>Wholesale Applications</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Business</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Business Type</TableHead>
              <TableHead>ABN</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {applications.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  No wholesale applications
                </TableCell>
              </TableRow>
            ) : (
              applications.map((app) => (
                <TableRow key={app.user_id}>
                  <TableCell>
                    <p className="font-medium">{app.business_name || app.wholesale_application?.business_name || "—"}</p>
                    {app.wholesale_application?.website && (
                      <a 
                        href={app.wholesale_application.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-primary hover:underline"
                      >
                        {app.wholesale_application.website}
                      </a>
                    )}
                  </TableCell>
                  <TableCell>
                    <div>
                      <p>{app.name}</p>
                      <p className="text-xs text-muted-foreground">{app.email}</p>
                    </div>
                  </TableCell>
                  <TableCell className="capitalize">
                    {app.wholesale_application?.business_type || "—"}
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    {app.wholesale_application?.abn || "—"}
                  </TableCell>
                  <TableCell>{getStatusBadge(app.wholesale_status)}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(app.created_at)}
                  </TableCell>
                  <TableCell>
                    {app.wholesale_status === 'pending' && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-emerald-600 border-emerald-600 hover:bg-emerald-50"
                          onClick={() => approveWholesale(app.user_id)}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-600 hover:bg-red-50"
                          onClick={() => rejectWholesale(app.user_id)}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    )}
                    {app.wholesale_status === 'approved' && (
                      <span className="text-sm text-muted-foreground">
                        {(app.wholesale_discount * 100).toFixed(0)}% discount
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default WholesaleTab;
